import { IColumn } from '@fluentui/react';
import { forwardRef, MouseEvent, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { KeyboardKey } from '../../models/components.models';
import { DetailsList, IDetailsListProps } from '../DetailsList/DetailsList';
import { Editor, FieldType, IEditorProps } from '../Editor/Editor';

export interface DetailsListEditorMeta {
  rowIndex: number;
  fieldName: string;
  item: { [key: string]: any };
}

interface FieldEdit {
  changed?: boolean;
  value?: any;
  fieldType?: FieldType;
  meta?: DetailsListEditorMeta;
}

type endEditHandler = (
  changed?: boolean,
  value?: any,
  rowIndex?: number,
  fieldName?: string,
  data?: { [key: string]: any }
) => void;

/**
 * Support ListRow component only.
 * Start Edit: by click a cell.
 * Edit the next cell: The tab key from Editor
 * End Edit: lose focus, Enter/Esc/Escape keys from Editor
 */

const getDepthPadding = (cellEl: HTMLElement) => {
  const depthEl = cellEl.querySelector('.h2o-ListCell-depth');
  if (!depthEl) {
    return 0;
  }
  const { left } = cellEl.getBoundingClientRect();
  const contentEl = cellEl.querySelector('.h2o-ListCell-content');
  const { left: contentLeft } = contentEl?.getBoundingClientRect() || { left: 0 };
  return contentLeft - left;
};

const getEditorProps = (cellEl?: HTMLElement, items?: any[], columns?: IColumn[]) => {
  if (cellEl && cellEl.classList.contains('is-editable') && items && columns) {
    const dataset = (cellEl as HTMLElement).dataset;
    const rowIndex = +(dataset.rowIndex || -1);
    const colIndex = +(dataset.colIndex || -1);
    if (rowIndex !== -1 && colIndex !== -1 && columns) {
      const column = columns[colIndex];
      const { data, fieldName = '' } = column;
      if (data?.listCellProps?.editable) {
        const { width, height } = cellEl.getBoundingClientRect();
        const depthPadding = colIndex === 0 ? getDepthPadding(cellEl) : 0;
        const editorProps = data.editorProps || {};
        const item = items[rowIndex];
        const value = item[fieldName];
        const offsetParent = cellEl.offsetParent as HTMLElement;
        const leftBorder = colIndex === 0 ? 0 : 1;
        return {
          ...editorProps,
          targetEl: cellEl,
          meta: { rowIndex, fieldName, item },
          value,
          width: width - depthPadding - leftBorder,
          height,
          left: cellEl.offsetLeft + depthPadding + leftBorder + 1,
          top: cellEl.offsetTop + (offsetParent?.offsetTop || 0),
          hidden: false,
        };
      }
    }
  }
  return { hidden: true };
};

const initEditorProps = () => getEditorProps();

const getNextEditableCell = (msRowOrPageEl: HTMLDivElement, isPage = false): HTMLDivElement | undefined => {
  if (!msRowOrPageEl) {
    return;
  }
  const nextMsRowOrPageEl = msRowOrPageEl.nextElementSibling as HTMLDivElement;
  if (nextMsRowOrPageEl) {
    const cellEl = nextMsRowOrPageEl?.querySelector(
      '.h2o-ListCell-root.is-editable:not(.is-disabled)'
    ) as HTMLDivElement;
    return cellEl ? cellEl : getNextEditableCell(nextMsRowOrPageEl, isPage);
  } else if (!isPage) {
    // when there is no rows, check the next page.
    return getNextEditableCell(msRowOrPageEl.closest('.ms-List-page') as HTMLDivElement, true);
  }
  return;
};

const moveToNextField = (cellEl?: HTMLDivElement) => {
  if (cellEl) {
    let hasEditableCell = false;
    while (cellEl?.nextElementSibling) {
      cellEl = cellEl.nextElementSibling as HTMLDivElement;
      if (cellEl.classList.contains('is-editable') && !cellEl.classList.contains('is-disabled')) {
        cellEl.scrollIntoView({ block: 'nearest' });
        cellEl.click();
        hasEditableCell = true;
        break;
      }
    }
    // check next rows and pages
    if (!hasEditableCell) {
      cellEl = getNextEditableCell(cellEl.closest('.ms-List-cell') as HTMLDivElement);
      if (cellEl) {
        cellEl.scrollIntoView({ block: 'nearest' });
        cellEl.click();
      }
    }
  }
};

/**
 * This is a utility function for DetailsListEditor.
 * For example, When you want to show and focus an editor of the first cell of a new row after adding a new row by clicking `Add a new row` button, you can use this.
 * @param detailsListContainerEl
 * @param rowSelector
 * @param cellSelector
 * @param focusLastRow
 */
export const focusAndEnableEditModeBySelector = (
  detailsListContainerEl: HTMLDivElement,
  rowSelector = '.is-empty-row',
  cellSelector = '.h2o-ListCell-root.is-editable:not(.is-disabled)',
  focusLastRow = false
) => {
  let rowEl;
  if (focusLastRow) {
    const rowEls = detailsListContainerEl.querySelectorAll(rowSelector);
    // last row
    rowEl = rowEls.length ? rowEls[rowEls.length - 1] : null;
  } else {
    rowEl = detailsListContainerEl.querySelector(rowSelector);
  }

  if (rowEl) {
    // first cell
    const cellEl: HTMLDivElement | null = rowEl.querySelector(cellSelector);
    if (cellEl) {
      cellEl.scrollIntoView({ block: 'nearest' });
      cellEl.click();
    }
  }
};

export interface IDetailsListEditorProps extends IDetailsListProps {
  optionalEditorFieldProps?: { [key: string]: any };
  onBeforeStartEdit?: (editorProps: IEditorProps) => void;
  onEndEdit?: endEditHandler;
  'data-test'?: string;
}

export const DetailsListEditor = forwardRef((props: IDetailsListEditorProps, ref: Ref<HTMLDivElement>) => {
  const { onClick, onEndEdit, optionalEditorFieldProps, onBeforeStartEdit, ...elementProps } = props;
  const { items, columns } = elementProps;
  const [editorProps, setEditorProps] = useState<IEditorProps>();
  const refEditor = useRef<HTMLDivElement>(null);
  const refMeta = useRef<{
    change: FieldEdit;
    cell?: HTMLDivElement;
    cellRect?: DOMRect;
    intervalId?: number;
    fieldProps?: { [key: string]: any };
  }>({
    change: {},
  });
  // DetailsList doesn't have ref for the container element
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  const endEdit = useCallback(() => {
    const { value, meta, changed } = refMeta.current.change;
    refMeta.current.change = {};
    setEditorProps(initEditorProps());
    if (onEndEdit) {
      const { rowIndex, fieldName, item } = meta || {};
      onEndEdit(changed, value, rowIndex, fieldName, item);
    }
  }, [onEndEdit]);
  // MutationObserver and ResizeObserver don't detect the location of an element. We need to use setInterval for checking the location of an element
  const endEditWhenCellLocationIsChanged = useCallback(
    (cellEl: HTMLDivElement) => {
      refMeta.current.cellRect = undefined;
      if (refMeta.current.intervalId !== undefined) {
        clearInterval(refMeta.current.intervalId);
      }
      if (cellEl) {
        const intervalId = window.setInterval(() => {
          const rect = refMeta.current.cellRect;
          const newRect = cellEl ? cellEl.getBoundingClientRect() : undefined;
          if (rect) {
            if (!newRect || rect.top !== newRect.top || rect.left !== newRect.left) {
              endEdit();
              clearInterval(intervalId);
              refMeta.current.intervalId = undefined;
            }
          }
          refMeta.current.cellRect = newRect;
        }, 300);
        refMeta.current.intervalId = intervalId;
      }
    },
    [endEdit]
  );
  const onDetailsListClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (onClick) {
        onClick(e);
      }
      endEdit();
      const cellEl = (e.target as HTMLElement).closest('.h2o-ListCell-root') as HTMLDivElement;
      refMeta.current.cell = cellEl;
      // start edit
      const editorProps = getEditorProps(cellEl, items, columns);
      if (onBeforeStartEdit) {
        refMeta.current.fieldProps = undefined;
        onBeforeStartEdit({ ...editorProps });
      }
      refMeta.current.change = { ...refMeta.current.change, fieldType: editorProps.fieldType };
      // delay for removing previous value
      setTimeout(() => {
        if (editorProps) {
          setEditorProps({ ...editorProps, fieldProps: { ...editorProps.fieldProps, ...refMeta.current.fieldProps } });
          endEditWhenCellLocationIsChanged(cellEl);
        }
      });
    },
    [onClick, endEdit, items, columns, endEditWhenCellLocationIsChanged, onBeforeStartEdit]
  );
  const onFieldChange = useCallback(
    (value?: any, meta?: DetailsListEditorMeta) => {
      refMeta.current.change = { ...refMeta.current.change, changed: true, value, meta };
      if (editorProps?.fieldType === FieldType.dropdown) {
        endEdit();
        moveToNextField(refMeta.current.cell);
      }
    },
    [editorProps?.fieldType, endEdit]
  );
  const onClickBody = useCallback(
    (e: globalThis.MouseEvent) => {
      const el = refRoot.current;
      const editorEl = refEditor.current;
      const listEl = el?.querySelector('.ms-List');
      const targetEl = e.target as HTMLElement;
      if (refMeta.current.change.fieldType === FieldType.dropdown) {
        // check the ms-Dropdown-callout
        const msDropdownCalloutEl = document.querySelector('.ms-Dropdown-callout');
        if (msDropdownCalloutEl && msDropdownCalloutEl.contains(targetEl)) {
          return;
        }
      }
      // click an element outside of the list
      if (listEl && editorEl && targetEl && !listEl.contains(targetEl) && !editorEl.contains(targetEl)) {
        endEdit();
      }
    },
    [endEdit]
  );
  const onKeyDownEditor = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e;
      switch (key) {
        case KeyboardKey.Tab:
          endEdit();
          moveToNextField(refMeta.current.cell);
          e.preventDefault();
          e.stopPropagation();
          break;
        case KeyboardKey.Enter:
          endEdit();
          break;
        case KeyboardKey.Esc:
        case KeyboardKey.Escape:
          setEditorProps(initEditorProps());
          break;
      }
    },
    [endEdit]
  );
  useEffect(() => {
    refMeta.current.fieldProps = optionalEditorFieldProps;
  }, [optionalEditorFieldProps]);
  useEffect(() => {
    document.addEventListener('mousedown', onClickBody, true);
    return () => {
      const intervalId = refMeta.current.intervalId;
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
      document.removeEventListener('mousedown', onClickBody, true);
    };
  }, [onClickBody]);
  return (
    <div
      ref={refRoot}
      data-test={props['data-test'] || 'details-list-editor'}
      style={{ height: '100%', overflow: 'auto', position: 'relative' }}
      data-is-scrollable="true"
    >
      <DetailsList {...elementProps} onClick={onDetailsListClick} />
      <Editor {...editorProps} ref={refEditor} onChange={onFieldChange} onKeyDown={onKeyDownEditor} />
    </div>
  );
});
